import { Timestamp } from '@angular/fire/firestore';
import { WspSendBlockedError } from './wsp-send-errors';

/* Mapa "messageBalance" dentro de users/{uid}/accounts/administration.
   Lo mantiene el backend; la web solo lo lee:
   - functions (OnPaymentComplete): cargan allowance en cada cobro de la suscripción y suman packageBalance al comprar un paquete
   - n8n: descuenta 1 por mensaje enviado, primero de allowance y después de packageBalance (ver docs/pedidos/n8n_saldo_mensajes.md) */
export interface MessageBalance {
    /* mensajes que quedan de paquetes comprados: no vencen */
    packageBalance: number;
    /* mensajes que quedan de los incluidos en la suscripción del período actual: se pierden al vencer el período o al cancelar */
    allowance: number;
    /* total de mensajes incluidos en el período (allowanceTotal - allowance = usados del plan). Se usa al subir de plan */
    allowanceTotal?: number;
    /* mensajes enviados por WhatsApp (web + agente) en el período actual. Solo estadística: no se resta */
    used: number;
    periodStart?: Timestamp;
    periodEnd?: Timestamp;
    /* id del cobro de MP con el que se renovó allowance por última vez (evita renovar dos veces) */
    lastRenewalPaymentId?: string;
}

/* Código de error que devuelve n8n cuando la administración no tiene saldo (ver docs/pedidos/n8n_saldo_mensajes.md):
   { success: false, error: { code: 'NO_MESSAGE_BALANCE', message, available, required } } */
export const NO_MESSAGE_BALANCE = 'NO_MESSAGE_BALANCE';

export function isNoMessageBalanceResponse(response: any): boolean {
    return response?.success === false && response?.error?.code === NO_MESSAGE_BALANCE;
}

/* Error de envío por falta de saldo. Lo lanza ChatsService (control previo o respuesta de n8n);
   las pantallas lo reconocen con instanceof WspSendBlockedError y muestran `toast` en lugar del error genérico */
export class NoMessageBalanceError extends WspSendBlockedError {
    constructor(
        public available: number = 0,
        public required: number = 1
    ) {
        super(
            required > 1
                ? `No tenés mensajes suficientes: necesitás ${required} y te quedan ${available}. Podés comprar más en "Comprar mensajes".`
                : 'No tenés mensajes disponibles. Podés comprar más en "Comprar mensajes".'
        );
        this.name = 'NoMessageBalanceError';
        Object.setPrototypeOf(this, NoMessageBalanceError.prototype);
    }

    get toast() {
        return {
            severity: 'warn',
            summary: 'Sin mensajes disponibles',
            detail: this.message,
            life: 8000,
        };
    }
}

/* Mensajes que le quedan disponibles a la administración. Sin mapa no hay nada disponible */
export function getAvailableMessages(
    balance?: Partial<MessageBalance> | null
): number {
    if (!balance) return 0;
    const { allowance = 0, packageBalance = 0 } = balance;
    return Math.max(0, allowance + packageBalance);
}
